import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { galleryAPI } from '../../services/api';
import { getStorageUrl } from '../../utils/imageHelpers';
import { Image, Video, Calendar, MapPin, Eye, X, Maximize2, ChevronLeft, ChevronRight } from 'lucide-react';

const GalleryContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: ${props => props.theme.colors.white};
  padding: 6rem 2rem 4rem 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  margin: 0 0 1rem 0;
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  margin: 0;
  opacity: 0.9;
`;

const Section = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  flex: 1;
  width: 100%;
`;

const AlbumsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AlbumCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }
`;

const AlbumCover = styled.div`
  width: 100%;
  height: 250px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.primary}40);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const AlbumOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  padding: 1rem;
  color: white;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  font-size: ${props => props.theme.typography.fontSize.sm};
  
  .media-counts {
    display: flex;
    gap: 1rem;
  }
  
  .view-hint {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${AlbumCard}:hover & .view-hint {
    opacity: 1;
  }
`;

const AlbumContent = styled.div`
  padding: 1.5rem;
`;

const AlbumTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.75rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AlbumDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AlbumMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  
  span {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2rem;
`;

const LightboxContent = styled.div`
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const LightboxHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
`;

const LightboxTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  margin: 0;
  color: white;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  max-height: 70vh;
  overflow-y: auto;
  padding: 1rem;
`;

const MediaItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.05);
    
    .fullscreen-hint {
      opacity: 1 !important;
    }
  }
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FullscreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.98);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  padding: 2rem;
`;

const FullscreenContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img, video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const FullscreenControls = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  display: flex;
  gap: 1rem;
  z-index: 20001;
`;

const FullscreenButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  transition: all 0.2s;
  z-index: 20001;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  ${props => props.left ? 'left: 2rem;' : 'right: 2rem;'}
`;

const GalleryPage = () => {
  const { theme } = useTheme();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumMedia, setAlbumMedia] = useState([]);
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  useEffect(() => {
    fetchAlbums();
  }, []);

  // Check for hash in URL and open album automatically
  useEffect(() => {
    if (albums.length > 0) {
      const hash = window.location.hash;
      if (hash.startsWith('#album-')) {
        const albumId = parseInt(hash.replace('#album-', ''));
        const album = albums.find(a => a.id === albumId);
        if (album) {
          openAlbum(album);
          // Clear hash after opening
          window.history.replaceState(null, null, ' ');
        }
      }
    }
  }, [albums]);

  const fetchAlbums = async () => {
    try {
      setLoading(true);
      const response = await galleryAPI.getAlbums({ status: 'published' });
      setAlbums(response.data || []);
    } catch (error) {
      console.error('Error fetching albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAlbum = async (album) => {
    try {
      setSelectedAlbum(album);
      const response = await galleryAPI.getMedia(album.id);
      setAlbumMedia(response.data || []);
    } catch (error) {
      console.error('Error fetching album media:', error);
    }
  };

  const closeAlbum = () => {
    setSelectedAlbum(null);
    setAlbumMedia([]);
  };

  const openFullscreen = (media, index) => {
    setFullscreenMedia(media);
    setFullscreenIndex(index);
  };

  const closeFullscreen = () => {
    setFullscreenMedia(null);
    setFullscreenIndex(0);
  };

  const nextMedia = () => {
    if (fullscreenIndex < albumMedia.length - 1) {
      const nextIndex = fullscreenIndex + 1;
      setFullscreenIndex(nextIndex);
      setFullscreenMedia(albumMedia[nextIndex]);
    }
  };

  const prevMedia = () => {
    if (fullscreenIndex > 0) {
      const prevIndex = fullscreenIndex - 1;
      setFullscreenIndex(prevIndex);
      setFullscreenMedia(albumMedia[prevIndex]);
    }
  };

  return (
    <GalleryContainer theme={theme}>
      <PublicHeader />
      
      <HeroSection theme={theme}>
        <HeroTitle theme={theme}>Photo & Video Gallery</HeroTitle>
        <HeroSubtitle>Capturing moments of our journey towards women's empowerment and human rights</HeroSubtitle>
      </HeroSection>

      <Section>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: theme.colors.text.secondary }}>
            Loading gallery...
          </div>
        ) : albums.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: theme.colors.text.secondary }}>
            No albums available yet.
          </div>
        ) : (
          <AlbumsGrid>
            {albums.map((album) => (
              <AlbumCard key={album.id} theme={theme}>
                <AlbumCover theme={theme} onClick={() => openAlbum(album)} style={{ cursor: 'pointer' }}>
                  {album.cover_image ? (
                    <img src={getStorageUrl(album.cover_image)} alt={album.title} />
                  ) : (
                    <Image size={80} />
                  )}
                  <AlbumOverlay theme={theme}>
                    <div className="media-counts">
                      <span><Image size={16} /> {album.images_count || 0}</span>
                      <span><Video size={16} /> {album.videos_count || 0}</span>
                    </div>
                    <div className="view-hint">
                      <Eye size={18} /> Click to View
                    </div>
                  </AlbumOverlay>
                </AlbumCover>
                
                <AlbumContent theme={theme}>
                  <AlbumTitle theme={theme}>{album.title}</AlbumTitle>
                  <AlbumDescription theme={theme}>{album.description}</AlbumDescription>
                  
                  <AlbumMeta theme={theme}>
                    {album.event_date && (
                      <span>
                        <Calendar size={16} />
                        {new Date(album.event_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    )}
                    {album.location && (
                      <span>
                        <MapPin size={16} />
                        {album.location}
                      </span>
                    )}
                  </AlbumMeta>
                  
                  <button
                    onClick={() => openAlbum(album)}
                    style={{
                      width: '100%',
                      marginTop: '1rem',
                      padding: '0.75rem 1rem',
                      background: theme.colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.target.style.background = theme.colors.primaryDark}
                    onMouseLeave={(e) => e.target.style.background = theme.colors.primary}
                  >
                    <Eye size={18} /> View Album ({(album.images_count || 0) + (album.videos_count || 0)} items)
                  </button>
                </AlbumContent>
              </AlbumCard>
            ))}
          </AlbumsGrid>
        )}
      </Section>

      {selectedAlbum && (
        <LightboxOverlay onClick={closeAlbum}>
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <LightboxHeader theme={theme}>
              <LightboxTitle theme={theme}>{selectedAlbum.title}</LightboxTitle>
              <CloseButton onClick={closeAlbum}>
                <X size={28} />
              </CloseButton>
            </LightboxHeader>
            
            <MediaGrid>
              {albumMedia.map((media, index) => (
                <MediaItem key={media.id} onClick={() => openFullscreen(media, index)}>
                  {media.media_type === 'image' ? (
                    <>
                      <img src={getStorageUrl(media.file_path)} alt={media.title || 'Gallery image'} />
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: 'rgba(0, 0, 0, 0.5)',
                        padding: '0.5rem',
                        borderRadius: '0.25rem',
                        display: 'flex',
                        opacity: 0,
                        transition: 'opacity 0.2s'
                      }}
                      className="fullscreen-hint">
                        <Maximize2 size={16} color="white" />
                      </div>
                    </>
                  ) : (
                    <iframe
                      src={media.video_url?.replace('watch?v=', 'embed/')}
                      title={media.title || 'Gallery video'}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ width: '100%', height: '100%' }}
                    />
                  )}
                </MediaItem>
              ))}
            </MediaGrid>
          </LightboxContent>
        </LightboxOverlay>
      )}

      {fullscreenMedia && (
        <FullscreenOverlay onClick={closeFullscreen}>
          <FullscreenContent onClick={(e) => e.stopPropagation()}>
            {fullscreenMedia.media_type === 'image' ? (
              <img 
                src={getStorageUrl(fullscreenMedia.file_path)} 
                alt={fullscreenMedia.title || 'Gallery image'} 
              />
            ) : (
              <iframe
                src={fullscreenMedia.video_url?.replace('watch?v=', 'embed/')}
                title={fullscreenMedia.title || 'Gallery video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '90%', height: '90%' }}
              />
            )}
            
            <FullscreenControls>
              <FullscreenButton onClick={closeFullscreen} title="Close">
                <X size={24} />
              </FullscreenButton>
            </FullscreenControls>
            
            <NavigationButton 
              left 
              onClick={prevMedia} 
              disabled={fullscreenIndex === 0}
              title="Previous"
            >
              <ChevronLeft size={32} />
            </NavigationButton>
            
            <NavigationButton 
              onClick={nextMedia} 
              disabled={fullscreenIndex === albumMedia.length - 1}
              title="Next"
            >
              <ChevronRight size={32} />
            </NavigationButton>
          </FullscreenContent>
        </FullscreenOverlay>
      )}

      <PublicFooter />
    </GalleryContainer>
  );
};

export default GalleryPage;

